import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export const AvailabilityCalendar = ({ booked = [], start, onPick, inventory = null, packageQty = 0 }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [cursor, setCursor] = useState(() => {
    const base = start || today;
    return { y: Number(base.slice(0, 4)), m: Number(base.slice(5, 7)) - 1 };
  });
  const { y, m } = cursor;
  const first = new Date(Date.UTC(y, m, 1));
  const startWeekday = first.getUTCDay();
  const daysInMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const monthName = first.toLocaleString("en-US", { month: "long", timeZone: "UTC" });
  const usedOn = (d) =>
    booked.filter((r) => d >= r.start_date && d <= r.end_date).reduce((s, r) => s + (r.tote_qty || 0), 0);
  const isBooked = (d) =>
    packageQty && inventory != null
      ? inventory - usedOn(d) < packageQty
      : booked.some((r) => d >= r.start_date && d <= r.end_date);
  const canPrev = iso(y, m, 1) > `${today.slice(0, 7)}-01`;

  const shift = (delta) => {
    const next = new Date(Date.UTC(y, m + delta, 1));
    setCursor({ y: next.getUTCFullYear(), m: next.getUTCMonth() });
  };

  const cells = [];
  for (let i = 0; i < startWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(iso(y, m, d));

  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-paper p-4" data-testid="availability-calendar">
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          {monthName} {y}
          <span className="mt-0.5 block normal-case tracking-normal text-slate-400">tap a day to set delivery</span>
        </p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => shift(-1)}
            disabled={!canPrev}
            aria-label="Previous month"
            data-testid="calendar-prev"
            className="grid h-7 w-7 place-items-center rounded-full border border-slate-200 bg-white text-ink transition-colors duration-200 hover:border-royal hover:text-royal disabled:opacity-30"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => shift(1)}
            aria-label="Next month"
            data-testid="calendar-next"
            className="grid h-7 w-7 place-items-center rounded-full border border-slate-200 bg-white text-ink transition-colors duration-200 hover:border-royal hover:text-royal"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((w) => (
          <span key={w} className="font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-slate-400">
            {w}
          </span>
        ))}
        {cells.map((d, i) =>
          d === null ? (
            <span key={`blank-${i}`} />
          ) : (
            <button
              key={d}
              type="button"
              disabled={d < today || isBooked(d)}
              onClick={() => onPick(d)}
              data-testid={`calendar-day-${d}`}
              className={`grid h-8 w-full place-items-center rounded-lg text-[11px] font-semibold transition-colors duration-150 ${
                d === start
                  ? "bg-royal text-white shadow-sm shadow-royal/30"
                  : isBooked(d)
                    ? "cursor-not-allowed bg-red-50 text-red-300 line-through"
                    : d < today
                      ? "cursor-not-allowed text-slate-300"
                      : "bg-white text-ink hover:border hover:border-royal hover:text-royal"
              }`}
            >
              {Number(d.slice(8, 10))}
            </button>
          )
        )}
      </div>
      <div className="mt-3 flex items-center gap-4 text-[10px] font-medium text-slate-500">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded bg-red-50 ring-1 ring-red-200" /> Reserved
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded bg-royal" /> Your delivery
        </span>
      </div>
    </div>
  );
};
